import { html } from "lit";
import { icons } from "../icons.js";

// Agent category types for filtering
export type AgentCategory =
  | "core"
  | "professional"
  | "sales"
  | "hr"
  | "finance"
  | "operations"
  | "it"
  | "customer"
  | "project"
  | "admin"
  | "training"
  | "compliance"
  | "realestate"
  | "manufacturing"
  | "retail"
  | "hospitality"
  | "healthcare"
  | "creative"
  | "analytics"
  | "engineering"
  | "support";

export type AgentCard = {
  icon: keyof typeof icons;
  name: string;
  role: string;
  description: string;
  prompt: string;
  category: AgentCategory;
};

// Category display info
export const AGENT_CATEGORIES: Record<AgentCategory, { label: string; order: number }> = {
  core: { label: "Core Agents", order: 0 },
  professional: { label: "Professional Services", order: 1 },
  sales: { label: "Sales & CRM", order: 2 },
  hr: { label: "HR & People", order: 3 },
  finance: { label: "Finance & Accounting", order: 4 },
  operations: { label: "Operations & Logistics", order: 5 },
  it: { label: "IT & Technology", order: 6 },
  customer: { label: "Customer Service", order: 7 },
  project: { label: "Project Management", order: 8 },
  admin: { label: "Administrative", order: 9 },
  training: { label: "Training & Education", order: 10 },
  compliance: { label: "Compliance & Risk", order: 11 },
  realestate: { label: "Real Estate", order: 12 },
  manufacturing: { label: "Manufacturing", order: 13 },
  retail: { label: "Retail & E-Commerce", order: 14 },
  hospitality: { label: "Hospitality & Events", order: 15 },
  healthcare: { label: "Healthcare Admin", order: 16 },
  creative: { label: "Creative & Design", order: 17 },
  analytics: { label: "Analytics & BI", order: 18 },
  engineering: { label: "Engineering", order: 19 },
  support: { label: "Quick Access", order: 99 },
};

const AGENTS: AgentCard[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE AGENTS (основные)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "brain",
    name: "George",
    role: "Lead Agent",
    description: "Your main AI assistant for any task or question",
    prompt: "Hey George, I need your help with:",
    category: "core",
  },
  {
    icon: "globe",
    name: "Scout",
    role: "Web Agent",
    description: "Searches the web and gathers live information",
    prompt: "Scout, please search and find information about:",
    category: "core",
  },
  {
    icon: "monitor",
    name: "Pilot",
    role: "Browser Agent",
    description: "Navigates websites and automates web tasks",
    prompt: "Pilot, I need you to go to a website and:",
    category: "core",
  },
  {
    icon: "image",
    name: "Vision",
    role: "Image Agent",
    description: "Analyzes images, screenshots, and visual content",
    prompt: "Vision, please look at this image and tell me:",
    category: "core",
  },
  {
    icon: "fileText",
    name: "Scribe",
    role: "Document Agent",
    description: "Reads and analyzes documents and files",
    prompt: "Scribe, please review this document and:",
    category: "core",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFESSIONAL SERVICES
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "scale",
    name: "Atticus",
    role: "Legal Agent",
    description: "Legal research, contracts, and compliance questions",
    prompt: "Atticus, I have a legal question about:",
    category: "professional",
  },
  {
    icon: "heart",
    name: "Healey",
    role: "Health Agent",
    description: "Medical information, wellness, and health guidance",
    prompt: "Healey, I need health advice regarding:",
    category: "professional",
  },
  {
    icon: "calculator",
    name: "Ledger",
    role: "CPA Agent",
    description: "Accounting, taxes, and financial guidance",
    prompt: "Ledger, I need help with my finances:",
    category: "professional",
  },
  {
    icon: "megaphone",
    name: "Maven",
    role: "Marketing Agent",
    description: "Marketing strategy, campaigns, and growth tactics",
    prompt: "Maven, I need marketing help with:",
    category: "professional",
  },
  {
    icon: "briefcase",
    name: "Baxter",
    role: "Business Consultant",
    description: "Strategic business advice and planning",
    prompt: "Baxter, I need business consulting on:",
    category: "professional",
  },
  {
    icon: "shield",
    name: "Sentinel",
    role: "Insurance Agent",
    description: "Insurance quotes, claims, and coverage advice",
    prompt: "Sentinel, help me with insurance for:",
    category: "professional",
  },
  {
    icon: "building",
    name: "Broker",
    role: "Investment Advisor",
    description: "Investment strategies and portfolio management",
    prompt: "Broker, I want investment advice on:",
    category: "professional",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SALES & CRM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "users",
    name: "Sterling",
    role: "CRM Manager",
    description: "Customer relationship management and lead tracking",
    prompt: "Sterling, help me manage my CRM:",
    category: "sales",
  },
  {
    icon: "target",
    name: "Hunter",
    role: "Lead Generator",
    description: "Find and qualify new business leads",
    prompt: "Hunter, I need leads for:",
    category: "sales",
  },
  {
    icon: "dollarSign",
    name: "Closer",
    role: "Sales Agent",
    description: "Sales scripts, objection handling, deal closing",
    prompt: "Closer, help me close this deal:",
    category: "sales",
  },
  {
    icon: "phone",
    name: "Dialer",
    role: "Cold Calling Agent",
    description: "Cold call scripts and outreach strategies",
    prompt: "Dialer, I need a cold call script for:",
    category: "sales",
  },
  {
    icon: "mail",
    name: "Outreach",
    role: "Email Sales Agent",
    description: "Sales email sequences and follow-ups",
    prompt: "Outreach, write a sales email for:",
    category: "sales",
  },
  {
    icon: "handshake",
    name: "Partner",
    role: "Partnership Agent",
    description: "Partnership development and joint ventures",
    prompt: "Partner, help me develop a partnership with:",
    category: "sales",
  },
  {
    icon: "gift",
    name: "Promo",
    role: "Promotions Agent",
    description: "Promotional campaigns and special offers",
    prompt: "Promo, create a promotion for:",
    category: "sales",
  },
  {
    icon: "trendingUp",
    name: "Forecast",
    role: "Sales Forecaster",
    description: "Sales predictions and pipeline analysis",
    prompt: "Forecast, analyze my sales pipeline:",
    category: "sales",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HR & PEOPLE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "user",
    name: "Harper",
    role: "HR Manager",
    description: "Human resources policies and employee relations",
    prompt: "Harper, I need HR help with:",
    category: "hr",
  },
  {
    icon: "dollarSign",
    name: "Payton",
    role: "Payroll Manager",
    description: "Payroll processing, wages, and deductions",
    prompt: "Payton, help me with payroll:",
    category: "hr",
  },
  {
    icon: "userPlus",
    name: "Recruiter",
    role: "Recruiting Agent",
    description: "Job postings, candidate screening, hiring",
    prompt: "Recruiter, help me hire for:",
    category: "hr",
  },
  {
    icon: "clipboard",
    name: "Onboard",
    role: "Onboarding Agent",
    description: "New employee onboarding and training setup",
    prompt: "Onboard, create an onboarding plan for:",
    category: "hr",
  },
  {
    icon: "star",
    name: "Performa",
    role: "Performance Agent",
    description: "Performance reviews and employee evaluations",
    prompt: "Performa, help with performance review for:",
    category: "hr",
  },
  {
    icon: "gift",
    name: "Benefits",
    role: "Benefits Agent",
    description: "Employee benefits, insurance, and perks",
    prompt: "Benefits, explain our benefits package:",
    category: "hr",
  },
  {
    icon: "calendar",
    name: "Scheduler",
    role: "Scheduling Agent",
    description: "Shift scheduling and time-off management",
    prompt: "Scheduler, help me schedule:",
    category: "hr",
  },
  {
    icon: "smile",
    name: "Culture",
    role: "Culture Agent",
    description: "Company culture and employee engagement",
    prompt: "Culture, improve our workplace culture:",
    category: "hr",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FINANCE & ACCOUNTING
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "creditCard",
    name: "Treasurer",
    role: "Treasury Agent",
    description: "Cash flow management and banking",
    prompt: "Treasurer, help with cash management:",
    category: "finance",
  },
  {
    icon: "fileText",
    name: "Invoicer",
    role: "Invoicing Agent",
    description: "Invoice creation and payment tracking",
    prompt: "Invoicer, create an invoice for:",
    category: "finance",
  },
  {
    icon: "dollarSign",
    name: "Collector",
    role: "Collections Agent",
    description: "Accounts receivable and debt collection",
    prompt: "Collector, help collect payment from:",
    category: "finance",
  },
  {
    icon: "pieChart",
    name: "Budgeter",
    role: "Budget Agent",
    description: "Budget planning and expense tracking",
    prompt: "Budgeter, create a budget for:",
    category: "finance",
  },
  {
    icon: "fileText",
    name: "Auditor",
    role: "Audit Agent",
    description: "Financial audits and compliance checks",
    prompt: "Auditor, audit our finances for:",
    category: "finance",
  },
  {
    icon: "trendingUp",
    name: "Investor",
    role: "Investment Agent",
    description: "Investment analysis and portfolio advice",
    prompt: "Investor, analyze this investment:",
    category: "finance",
  },
  {
    icon: "calculator",
    name: "Taxman",
    role: "Tax Agent",
    description: "Tax preparation and tax planning strategies",
    prompt: "Taxman, help with taxes for:",
    category: "finance",
  },
  {
    icon: "barChart",
    name: "Analyst",
    role: "Financial Analyst",
    description: "Financial modeling and analysis",
    prompt: "Analyst, analyze financials for:",
    category: "finance",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OPERATIONS & LOGISTICS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "truck",
    name: "Shipper",
    role: "Shipping Agent",
    description: "Shipping logistics and carrier management",
    prompt: "Shipper, arrange shipping for:",
    category: "operations",
  },
  {
    icon: "package",
    name: "Inventory",
    role: "Inventory Agent",
    description: "Inventory management and stock tracking",
    prompt: "Inventory, check stock levels for:",
    category: "operations",
  },
  {
    icon: "map",
    name: "Router",
    role: "Route Planner",
    description: "Delivery route optimization",
    prompt: "Router, plan the best route for:",
    category: "operations",
  },
  {
    icon: "warehouse",
    name: "Warehouse",
    role: "Warehouse Agent",
    description: "Warehouse operations and storage",
    prompt: "Warehouse, organize storage for:",
    category: "operations",
  },
  {
    icon: "shoppingCart",
    name: "Procurer",
    role: "Procurement Agent",
    description: "Vendor sourcing and purchasing",
    prompt: "Procurer, find suppliers for:",
    category: "operations",
  },
  {
    icon: "link",
    name: "Supply",
    role: "Supply Chain Agent",
    description: "Supply chain optimization and management",
    prompt: "Supply, optimize our supply chain for:",
    category: "operations",
  },
  {
    icon: "clock",
    name: "Expediter",
    role: "Expediting Agent",
    description: "Rush orders and priority handling",
    prompt: "Expediter, expedite this order:",
    category: "operations",
  },
  {
    icon: "checkCircle",
    name: "Quality",
    role: "QA Agent",
    description: "Quality assurance and inspection",
    prompt: "Quality, check quality for:",
    category: "operations",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // IT & TECHNOLOGY
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "code",
    name: "Coder",
    role: "Developer Agent",
    description: "Code writing, debugging, and development",
    prompt: "Coder, help me code:",
    category: "it",
  },
  {
    icon: "terminal",
    name: "DevOps",
    role: "DevOps Agent",
    description: "CI/CD, deployment, and infrastructure",
    prompt: "DevOps, help with deployment:",
    category: "it",
  },
  {
    icon: "shield",
    name: "Security",
    role: "Security Agent",
    description: "Cybersecurity and threat protection",
    prompt: "Security, analyze security for:",
    category: "it",
  },
  {
    icon: "database",
    name: "DataBase",
    role: "Database Agent",
    description: "Database design and SQL queries",
    prompt: "DataBase, help with my database:",
    category: "it",
  },
  {
    icon: "cloud",
    name: "Cloud",
    role: "Cloud Agent",
    description: "Cloud services and infrastructure",
    prompt: "Cloud, set up cloud services for:",
    category: "it",
  },
  {
    icon: "wifi",
    name: "Network",
    role: "Network Agent",
    description: "Network configuration and troubleshooting",
    prompt: "Network, fix network issues:",
    category: "it",
  },
  {
    icon: "smartphone",
    name: "Mobile",
    role: "Mobile Dev Agent",
    description: "Mobile app development",
    prompt: "Mobile, build a mobile app for:",
    category: "it",
  },
  {
    icon: "helpCircle",
    name: "HelpDesk",
    role: "IT Support Agent",
    description: "Technical support and troubleshooting",
    prompt: "HelpDesk, I have a tech issue:",
    category: "it",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOMER SERVICE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "messageCircle",
    name: "Supporta",
    role: "Support Agent",
    description: "Customer support and issue resolution",
    prompt: "Supporta, help with customer issue:",
    category: "customer",
  },
  {
    icon: "phone",
    name: "Callista",
    role: "Call Center Agent",
    description: "Phone support scripts and handling",
    prompt: "Callista, handle this call:",
    category: "customer",
  },
  {
    icon: "mail",
    name: "Responder",
    role: "Email Support Agent",
    description: "Email support responses",
    prompt: "Responder, draft a response for:",
    category: "customer",
  },
  {
    icon: "messageSquare",
    name: "Chatter",
    role: "Live Chat Agent",
    description: "Live chat customer support",
    prompt: "Chatter, help with live chat:",
    category: "customer",
  },
  {
    icon: "alertCircle",
    name: "Escalator",
    role: "Escalation Agent",
    description: "Handle escalated customer issues",
    prompt: "Escalator, this issue needs escalation:",
    category: "customer",
  },
  {
    icon: "star",
    name: "Reviewer",
    role: "Review Manager",
    description: "Manage customer reviews and feedback",
    prompt: "Reviewer, respond to this review:",
    category: "customer",
  },
  {
    icon: "award",
    name: "Loyalty",
    role: "Loyalty Agent",
    description: "Customer loyalty programs and rewards",
    prompt: "Loyalty, create a loyalty program:",
    category: "customer",
  },
  {
    icon: "refreshCw",
    name: "Returns",
    role: "Returns Agent",
    description: "Handle returns and refunds",
    prompt: "Returns, process this return:",
    category: "customer",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROJECT MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "folder",
    name: "Project",
    role: "Project Manager",
    description: "Project planning and execution",
    prompt: "Project, help me manage this project:",
    category: "project",
  },
  {
    icon: "list",
    name: "Tasker",
    role: "Task Manager",
    description: "Task breakdown and assignment",
    prompt: "Tasker, break down tasks for:",
    category: "project",
  },
  {
    icon: "users",
    name: "Scrum",
    role: "Scrum Master",
    description: "Agile methodologies and sprints",
    prompt: "Scrum, plan a sprint for:",
    category: "project",
  },
  {
    icon: "calendar",
    name: "Timeline",
    role: "Timeline Agent",
    description: "Project timelines and Gantt charts",
    prompt: "Timeline, create a timeline for:",
    category: "project",
  },
  {
    icon: "alertTriangle",
    name: "Risk",
    role: "Risk Manager",
    description: "Project risk assessment and mitigation",
    prompt: "Risk, assess risks for:",
    category: "project",
  },
  {
    icon: "dollarSign",
    name: "Estimator",
    role: "Cost Estimator",
    description: "Project cost estimation",
    prompt: "Estimator, estimate costs for:",
    category: "project",
  },
  {
    icon: "flag",
    name: "Milestone",
    role: "Milestone Tracker",
    description: "Track project milestones and goals",
    prompt: "Milestone, set milestones for:",
    category: "project",
  },
  {
    icon: "users",
    name: "Resource",
    role: "Resource Planner",
    description: "Resource allocation and capacity",
    prompt: "Resource, allocate resources for:",
    category: "project",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMINISTRATIVE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "calendar",
    name: "Calendar",
    role: "Calendar Agent",
    description: "Meeting scheduling and calendar management",
    prompt: "Calendar, schedule a meeting for:",
    category: "admin",
  },
  {
    icon: "mail",
    name: "Mailer",
    role: "Email Agent",
    description: "Email drafting and management",
    prompt: "Mailer, draft an email for:",
    category: "admin",
  },
  {
    icon: "fileText",
    name: "Documenta",
    role: "Documentation Agent",
    description: "Document creation and formatting",
    prompt: "Documenta, create a document for:",
    category: "admin",
  },
  {
    icon: "archive",
    name: "Filer",
    role: "Filing Agent",
    description: "File organization and records management",
    prompt: "Filer, organize files for:",
    category: "admin",
  },
  {
    icon: "printer",
    name: "Print",
    role: "Print Agent",
    description: "Print jobs and document preparation",
    prompt: "Print, prepare documents for printing:",
    category: "admin",
  },
  {
    icon: "phone",
    name: "Reception",
    role: "Receptionist Agent",
    description: "Call handling and visitor management",
    prompt: "Reception, handle calls for:",
    category: "admin",
  },
  {
    icon: "mapPin",
    name: "Travel",
    role: "Travel Agent",
    description: "Travel booking and itinerary planning",
    prompt: "Travel, book travel for:",
    category: "admin",
  },
  {
    icon: "clipboard",
    name: "Minutes",
    role: "Meeting Notes Agent",
    description: "Meeting minutes and action items",
    prompt: "Minutes, take notes for:",
    category: "admin",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TRAINING & EDUCATION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "book",
    name: "Trainer",
    role: "Training Agent",
    description: "Employee training and skill development",
    prompt: "Trainer, create training for:",
    category: "training",
  },
  {
    icon: "video",
    name: "Instructor",
    role: "Video Instructor",
    description: "Video training and tutorials",
    prompt: "Instructor, create a tutorial for:",
    category: "training",
  },
  {
    icon: "award",
    name: "Certifier",
    role: "Certification Agent",
    description: "Certifications and compliance training",
    prompt: "Certifier, set up certification for:",
    category: "training",
  },
  {
    icon: "clipboard",
    name: "Assessor",
    role: "Assessment Agent",
    description: "Skills assessment and testing",
    prompt: "Assessor, assess skills for:",
    category: "training",
  },
  {
    icon: "users",
    name: "Coach",
    role: "Coaching Agent",
    description: "One-on-one coaching and mentoring",
    prompt: "Coach, help me coach:",
    category: "training",
  },
  {
    icon: "book",
    name: "Curriculum",
    role: "Curriculum Agent",
    description: "Training curriculum development",
    prompt: "Curriculum, develop curriculum for:",
    category: "training",
  },
  {
    icon: "play",
    name: "eLearning",
    role: "E-Learning Agent",
    description: "Online course creation",
    prompt: "eLearning, create an online course for:",
    category: "training",
  },
  {
    icon: "smile",
    name: "Motivator",
    role: "Motivation Agent",
    description: "Team motivation and engagement",
    prompt: "Motivator, boost morale for:",
    category: "training",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPLIANCE & RISK
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "shield",
    name: "Compliance",
    role: "Compliance Agent",
    description: "Regulatory compliance and policies",
    prompt: "Compliance, check compliance for:",
    category: "compliance",
  },
  {
    icon: "lock",
    name: "Privacy",
    role: "Privacy Agent",
    description: "Data privacy and GDPR compliance",
    prompt: "Privacy, ensure privacy for:",
    category: "compliance",
  },
  {
    icon: "alertTriangle",
    name: "RiskMgr",
    role: "Risk Manager",
    description: "Enterprise risk management",
    prompt: "RiskMgr, assess enterprise risk for:",
    category: "compliance",
  },
  {
    icon: "fileText",
    name: "Policy",
    role: "Policy Agent",
    description: "Policy creation and documentation",
    prompt: "Policy, create a policy for:",
    category: "compliance",
  },
  {
    icon: "eye",
    name: "Monitor",
    role: "Monitoring Agent",
    description: "Compliance monitoring and reporting",
    prompt: "Monitor, monitor compliance for:",
    category: "compliance",
  },
  {
    icon: "clipboard",
    name: "Audita",
    role: "Audit Agent",
    description: "Internal audits and assessments",
    prompt: "Audita, conduct an audit of:",
    category: "compliance",
  },
  {
    icon: "shield",
    name: "Safety",
    role: "Safety Agent",
    description: "Workplace safety and OSHA compliance",
    prompt: "Safety, check safety protocols for:",
    category: "compliance",
  },
  {
    icon: "globe",
    name: "RegWatch",
    role: "Regulatory Agent",
    description: "Track regulatory changes",
    prompt: "RegWatch, check regulations for:",
    category: "compliance",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REAL ESTATE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "building",
    name: "Realtor",
    role: "Real Estate Agent",
    description: "Property listings and sales",
    prompt: "Realtor, find properties for:",
    category: "realestate",
  },
  {
    icon: "home",
    name: "Homefinder",
    role: "Home Search Agent",
    description: "Home search and recommendations",
    prompt: "Homefinder, search homes for:",
    category: "realestate",
  },
  {
    icon: "dollarSign",
    name: "Appraiser",
    role: "Appraisal Agent",
    description: "Property valuation and appraisals",
    prompt: "Appraiser, appraise this property:",
    category: "realestate",
  },
  {
    icon: "key",
    name: "Landlord",
    role: "Property Manager",
    description: "Rental property management",
    prompt: "Landlord, manage property:",
    category: "realestate",
  },
  {
    icon: "users",
    name: "Tenant",
    role: "Tenant Agent",
    description: "Tenant screening and relations",
    prompt: "Tenant, screen tenant for:",
    category: "realestate",
  },
  {
    icon: "fileText",
    name: "Lease",
    role: "Leasing Agent",
    description: "Lease agreements and negotiations",
    prompt: "Lease, draft a lease for:",
    category: "realestate",
  },
  {
    icon: "hammer",
    name: "Inspector",
    role: "Property Inspector",
    description: "Property inspections and reports",
    prompt: "Inspector, inspect property:",
    category: "realestate",
  },
  {
    icon: "calculator",
    name: "Mortgage",
    role: "Mortgage Agent",
    description: "Mortgage calculations and advice",
    prompt: "Mortgage, calculate mortgage for:",
    category: "realestate",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MANUFACTURING
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "settings",
    name: "Factory",
    role: "Production Agent",
    description: "Production planning and scheduling",
    prompt: "Factory, plan production for:",
    category: "manufacturing",
  },
  {
    icon: "tool",
    name: "Maintenance",
    role: "Maintenance Agent",
    description: "Equipment maintenance and repairs",
    prompt: "Maintenance, schedule maintenance for:",
    category: "manufacturing",
  },
  {
    icon: "checkCircle",
    name: "QualityCtrl",
    role: "Quality Control",
    description: "Manufacturing quality standards",
    prompt: "QualityCtrl, check quality for:",
    category: "manufacturing",
  },
  {
    icon: "package",
    name: "Materials",
    role: "Materials Agent",
    description: "Raw materials and BOM management",
    prompt: "Materials, order materials for:",
    category: "manufacturing",
  },
  {
    icon: "cpu",
    name: "Automation",
    role: "Automation Agent",
    description: "Factory automation and robotics",
    prompt: "Automation, automate process for:",
    category: "manufacturing",
  },
  {
    icon: "clipboard",
    name: "WorkOrder",
    role: "Work Order Agent",
    description: "Work order management",
    prompt: "WorkOrder, create work order for:",
    category: "manufacturing",
  },
  {
    icon: "thermometer",
    name: "Process",
    role: "Process Engineer",
    description: "Manufacturing process optimization",
    prompt: "Process, optimize process for:",
    category: "manufacturing",
  },
  {
    icon: "shield",
    name: "PlantSafe",
    role: "Plant Safety Agent",
    description: "Manufacturing safety protocols",
    prompt: "PlantSafe, ensure safety for:",
    category: "manufacturing",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RETAIL & E-COMMERCE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "shoppingCart",
    name: "Shopper",
    role: "E-Commerce Agent",
    description: "Online store management",
    prompt: "Shopper, optimize my store:",
    category: "retail",
  },
  {
    icon: "tag",
    name: "Pricer",
    role: "Pricing Agent",
    description: "Pricing strategy and optimization",
    prompt: "Pricer, set pricing for:",
    category: "retail",
  },
  {
    icon: "package",
    name: "Merch",
    role: "Merchandising Agent",
    description: "Product merchandising and displays",
    prompt: "Merch, plan merchandising for:",
    category: "retail",
  },
  {
    icon: "percent",
    name: "Discount",
    role: "Discount Agent",
    description: "Discounts and promotional pricing",
    prompt: "Discount, create discount for:",
    category: "retail",
  },
  {
    icon: "barChart",
    name: "RetailAnalyst",
    role: "Retail Analytics",
    description: "Retail performance analytics",
    prompt: "RetailAnalyst, analyze sales for:",
    category: "retail",
  },
  {
    icon: "box",
    name: "Fulfillment",
    role: "Fulfillment Agent",
    description: "Order fulfillment and shipping",
    prompt: "Fulfillment, fulfill orders for:",
    category: "retail",
  },
  {
    icon: "star",
    name: "Product",
    role: "Product Agent",
    description: "Product descriptions and listings",
    prompt: "Product, create listing for:",
    category: "retail",
  },
  {
    icon: "shoppingBag",
    name: "POS",
    role: "POS Agent",
    description: "Point of sale operations",
    prompt: "POS, help with checkout:",
    category: "retail",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HOSPITALITY & EVENTS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "calendar",
    name: "Event",
    role: "Event Planner",
    description: "Event planning and coordination",
    prompt: "Event, plan an event for:",
    category: "hospitality",
  },
  {
    icon: "home",
    name: "Concierge",
    role: "Concierge Agent",
    description: "Guest services and recommendations",
    prompt: "Concierge, assist guest with:",
    category: "hospitality",
  },
  {
    icon: "bed",
    name: "Booking",
    role: "Reservation Agent",
    description: "Room bookings and reservations",
    prompt: "Booking, make a reservation for:",
    category: "hospitality",
  },
  {
    icon: "coffee",
    name: "Catering",
    role: "Catering Agent",
    description: "Food and beverage planning",
    prompt: "Catering, plan catering for:",
    category: "hospitality",
  },
  {
    icon: "users",
    name: "Venue",
    role: "Venue Agent",
    description: "Venue selection and booking",
    prompt: "Venue, find a venue for:",
    category: "hospitality",
  },
  {
    icon: "music",
    name: "Entertainment",
    role: "Entertainment Agent",
    description: "Entertainment booking",
    prompt: "Entertainment, book entertainment for:",
    category: "hospitality",
  },
  {
    icon: "camera",
    name: "PhotoVideo",
    role: "Photo/Video Agent",
    description: "Event photography and videography",
    prompt: "PhotoVideo, arrange coverage for:",
    category: "hospitality",
  },
  {
    icon: "gift",
    name: "GiftBag",
    role: "Swag Agent",
    description: "Gift bags and promotional items",
    prompt: "GiftBag, create gift bags for:",
    category: "hospitality",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HEALTHCARE ADMIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "clipboard",
    name: "Intake",
    role: "Patient Intake",
    description: "Patient registration and intake",
    prompt: "Intake, register patient:",
    category: "healthcare",
  },
  {
    icon: "calendar",
    name: "Appointment",
    role: "Scheduling Agent",
    description: "Medical appointment scheduling",
    prompt: "Appointment, schedule appointment for:",
    category: "healthcare",
  },
  {
    icon: "fileText",
    name: "MedRecord",
    role: "Records Agent",
    description: "Medical records management",
    prompt: "MedRecord, update records for:",
    category: "healthcare",
  },
  {
    icon: "creditCard",
    name: "Billing",
    role: "Medical Billing",
    description: "Medical billing and coding",
    prompt: "Billing, process billing for:",
    category: "healthcare",
  },
  {
    icon: "shield",
    name: "HIPAA",
    role: "HIPAA Agent",
    description: "HIPAA compliance and privacy",
    prompt: "HIPAA, ensure compliance for:",
    category: "healthcare",
  },
  {
    icon: "phone",
    name: "PatientCall",
    role: "Patient Outreach",
    description: "Patient follow-ups and reminders",
    prompt: "PatientCall, follow up with:",
    category: "healthcare",
  },
  {
    icon: "pill",
    name: "Pharmacy",
    role: "Pharmacy Agent",
    description: "Prescription management",
    prompt: "Pharmacy, check prescription for:",
    category: "healthcare",
  },
  {
    icon: "heart",
    name: "Wellness",
    role: "Wellness Agent",
    description: "Wellness programs and preventive care",
    prompt: "Wellness, create wellness plan for:",
    category: "healthcare",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CREATIVE & DESIGN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "palette",
    name: "Designer",
    role: "Graphic Designer",
    description: "Visual design and graphics",
    prompt: "Designer, create a design for:",
    category: "creative",
  },
  {
    icon: "edit",
    name: "Copywriter",
    role: "Copywriting Agent",
    description: "Marketing copy and content writing",
    prompt: "Copywriter, write copy for:",
    category: "creative",
  },
  {
    icon: "video",
    name: "VideoMaker",
    role: "Video Producer",
    description: "Video production and editing",
    prompt: "VideoMaker, produce a video for:",
    category: "creative",
  },
  {
    icon: "camera",
    name: "Photographer",
    role: "Photography Agent",
    description: "Photo shoots and image selection",
    prompt: "Photographer, plan photos for:",
    category: "creative",
  },
  {
    icon: "layout",
    name: "UIDesigner",
    role: "UI/UX Agent",
    description: "User interface and experience design",
    prompt: "UIDesigner, design UI for:",
    category: "creative",
  },
  {
    icon: "type",
    name: "Branding",
    role: "Brand Agent",
    description: "Brand identity and guidelines",
    prompt: "Branding, develop brand for:",
    category: "creative",
  },
  {
    icon: "music",
    name: "Audio",
    role: "Audio Producer",
    description: "Audio production and podcasts",
    prompt: "Audio, produce audio for:",
    category: "creative",
  },
  {
    icon: "share2",
    name: "Social",
    role: "Social Media Agent",
    description: "Social media content and strategy",
    prompt: "Social, create social content for:",
    category: "creative",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICS & BI
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "barChart",
    name: "DataViz",
    role: "Data Visualization",
    description: "Charts, dashboards, and reports",
    prompt: "DataViz, create a dashboard for:",
    category: "analytics",
  },
  {
    icon: "database",
    name: "DataMiner",
    role: "Data Mining Agent",
    description: "Data extraction and patterns",
    prompt: "DataMiner, analyze data for:",
    category: "analytics",
  },
  {
    icon: "trendingUp",
    name: "Predictor",
    role: "Predictive Analytics",
    description: "Forecasting and predictions",
    prompt: "Predictor, predict trends for:",
    category: "analytics",
  },
  {
    icon: "pieChart",
    name: "Reporter",
    role: "Reporting Agent",
    description: "Business reports and KPIs",
    prompt: "Reporter, generate report for:",
    category: "analytics",
  },
  {
    icon: "activity",
    name: "Metrics",
    role: "Metrics Agent",
    description: "Performance metrics and tracking",
    prompt: "Metrics, track metrics for:",
    category: "analytics",
  },
  {
    icon: "cpu",
    name: "MLAgent",
    role: "Machine Learning Agent",
    description: "ML models and AI insights",
    prompt: "MLAgent, build a model for:",
    category: "analytics",
  },
  {
    icon: "search",
    name: "Insights",
    role: "Insights Agent",
    description: "Business intelligence insights",
    prompt: "Insights, find insights for:",
    category: "analytics",
  },
  {
    icon: "target",
    name: "AB Test",
    role: "A/B Testing Agent",
    description: "Experiment design and analysis",
    prompt: "AB Test, set up a test for:",
    category: "analytics",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENGINEERING
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "tool",
    name: "Mechanical",
    role: "Mechanical Engineer",
    description: "Mechanical design and analysis",
    prompt: "Mechanical, design system for:",
    category: "engineering",
  },
  {
    icon: "zap",
    name: "Electrical",
    role: "Electrical Engineer",
    description: "Electrical systems and circuits",
    prompt: "Electrical, design circuit for:",
    category: "engineering",
  },
  {
    icon: "building",
    name: "Civil",
    role: "Civil Engineer",
    description: "Structural and civil engineering",
    prompt: "Civil, analyze structure for:",
    category: "engineering",
  },
  {
    icon: "droplet",
    name: "Chemical",
    role: "Chemical Engineer",
    description: "Chemical processes and materials",
    prompt: "Chemical, analyze process for:",
    category: "engineering",
  },
  {
    icon: "cpu",
    name: "Systems",
    role: "Systems Engineer",
    description: "Systems integration and architecture",
    prompt: "Systems, design system for:",
    category: "engineering",
  },
  {
    icon: "compass",
    name: "CAD",
    role: "CAD Agent",
    description: "Computer-aided design and drafting",
    prompt: "CAD, create drawing for:",
    category: "engineering",
  },
  {
    icon: "thermometer",
    name: "HVAC",
    role: "HVAC Engineer",
    description: "Heating, ventilation, and AC",
    prompt: "HVAC, design HVAC for:",
    category: "engineering",
  },
  {
    icon: "leaf",
    name: "Environmental",
    role: "Environmental Engineer",
    description: "Environmental impact and sustainability",
    prompt: "Environmental, assess impact for:",
    category: "engineering",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // QUICK ACCESS (support)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    icon: "zap",
    name: "Quick Task",
    role: "Any Agent",
    description: "Just ask anything — the right agent will help",
    prompt: "",
    category: "support",
  },
];

export type ChatWelcomeProps = {
  onSelectSuggestion: (prompt: string) => void;
  connected: boolean;
};

export function renderChatWelcome(props: ChatWelcomeProps) {
  // Group agents by category
  const agentsByCategory = AGENTS.reduce(
    (acc, agent) => {
      if (!acc[agent.category]) {
        acc[agent.category] = [];
      }
      acc[agent.category].push(agent);
      return acc;
    },
    {} as Record<AgentCategory, AgentCard[]>,
  );

  // Sort categories by order
  const sortedCategories = (Object.keys(agentsByCategory) as AgentCategory[]).toSorted(
    (a, b) => AGENT_CATEGORIES[a].order - AGENT_CATEGORIES[b].order,
  );

  const renderAgentCard = (agent: AgentCard) => html`
    <button
      class="welcome-card"
      @click=${() => props.onSelectSuggestion(agent.prompt)}
      ?disabled=${!props.connected}
    >
      <div class="welcome-card__icon">${icons[agent.icon]}</div>
      <div class="welcome-card__content">
        <div class="welcome-card__name">${agent.name}</div>
        <div class="welcome-card__role">${agent.role}</div>
        <div class="welcome-card__desc">${agent.description}</div>
      </div>
    </button>
  `;

  const renderCategory = (category: AgentCategory) => {
    const agents = agentsByCategory[category];
    const info = AGENT_CATEGORIES[category];

    // Support category gets special centered layout
    if (category === "support") {
      return html`
        <div class="chat-welcome__section">
          <div class="chat-welcome__grid chat-welcome__grid--centered">
            ${agents.map(renderAgentCard)}
          </div>
        </div>
      `;
    }

    return html`
      <div class="chat-welcome__section">
        <div class="chat-welcome__section-title">${info.label}</div>
        <div class="chat-welcome__grid">
          ${agents.map(renderAgentCard)}
        </div>
      </div>
    `;
  };

  return html`
    <div class="chat-welcome">
      <div class="chat-welcome__header">
        <h2 class="chat-welcome__title">Meet Your Agent Swarm</h2>
        <p class="chat-welcome__subtitle">
          ${AGENTS.length} AI agents ready to help. Click an agent or just type your message.
        </p>
      </div>

      ${sortedCategories.map(renderCategory)}
    </div>
  `;
}
